'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { FaWallet } from 'react-icons/fa';
import styles from './WalletDepositModal.module.css';

export function openLightningWallet(invoice) {
  if (typeof window === 'undefined' || !invoice) return;
  window.location.href = `lightning:${invoice}`;
}

async function copyText(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(String(text));
    return true;
  } catch {
    return false;
  }
}

function CopyField({ label, value, singleLine = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!(await copyText(value))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div className={styles.copyRow}>
        <span
          className={`${styles.value} ${singleLine ? styles.singleLine : ''}`}
          title={singleLine ? value : undefined}
        >
          {value}
        </span>
        <button type="button" className={styles.copyBtn} onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default function WalletDepositModal({ deposit, onClose }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!mounted || !deposit?.cryptoAddress) return null;

  const invoice = deposit.cryptoAddress;
  const cryptoAmount = deposit.amountTo_Str || String(deposit.amountTo ?? '');

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="walletDepositTitle"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 id="walletDepositTitle" className={styles.title}>
          Scan to deposit
        </h2>

        <div className={styles.qr}>
          <QRCodeSVG level="M" size={220} value={`lightning:${invoice}`} />
        </div>

        <CopyField label="Crypto Amount" value={`${cryptoAmount} SATS`} />
        <CopyField label="Deposit Address" value={invoice} singleLine />

        <a className={styles.openBtn} href={`lightning:${invoice}`}>
          <FaWallet aria-hidden="true" />
          Open in wallet
        </a>

        <p className={styles.hint}>
          No crypto wallet detected on this device. Scan the QR with your phone
          wallet or copy the address above.
        </p>

        {deposit.conversionID != null && (
          <div className={styles.meta}>
            <span className={styles.label}>Conversion ID</span>
            <span className={styles.metaValue}>{deposit.conversionID}</span>
          </div>
        )}
        {deposit.transactionID && (
          <div className={styles.meta}>
            <span className={styles.label}>Transaction ID</span>
            <span className={styles.metaValue}>{deposit.transactionID}</span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
